import { jsx as _jsx } from "react/jsx-runtime";
/**
 * WorkflowBuilderContext
 *
 * Global state management for the visual workflow builder.
 * Uses React Context + useReducer for predictable state updates.
 *
 * Features:
 * - Canvas state: steps, connections, selection, zoom, pan
 * - Undo / redo via action history stack
 * - Auto-save draft to localStorage (debounced 5 s)
 * - Validation on every state mutation
 * - Save / load workflow via API
 *
 * Requirements: 1.2, 1.3, 1.5, 1.6, 5.1, 5.2, 12.7, 13.1–13.6
 */
import { createContext, useContext, useReducer, useCallback, useEffect, useRef, } from 'react';
import { validateWorkflow } from '@/utils/workflowValidator';
import { autoLayout } from '@/utils/canvasLayout';
import { workflowBuilderApi } from '@/api/workflowBuilderApi';
const EMPTY_VALIDATION = { isValid: true, errors: [], warnings: [] };
const INITIAL_CANVAS = {
    steps: [],
    connections: [],
    selectedStepId: null,
    zoom: 1,
    pan: { x: 0, y: 0 },
};
const INITIAL_STATE = {
    currentWorkflow: null,
    canvasState: INITIAL_CANVAS,
    availableSteps: [],
    versions: [],
    isDirty: false,
    validationErrors: EMPTY_VALIDATION,
    undoStack: [],
    redoStack: [],
    isLoading: false,
    isSaving: false,
    error: null,
};
// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function generateId() {
    // crypto.randomUUID is available in all modern browsers
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    // Fallback for test environments
    return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}
const AUTO_SAVE_DELAY = 5000; // 5 seconds
const MAX_UNDO = 50;
function draftKey(workflowId) {
    return `workflow-draft-${workflowId}`;
}
// ---------------------------------------------------------------------------
// Reducer helpers
// ---------------------------------------------------------------------------
/** Push current canvas onto undo stack, clear redo. */
function pushUndo(state) {
    const stack = [...state.undoStack, state.canvasState];
    return {
        undoStack: stack.length > MAX_UNDO ? stack.slice(stack.length - MAX_UNDO) : stack,
        redoStack: [],
    };
}
/** Build a BuilderWorkflowDefinition from current state for validation. */
function buildWorkflowForValidation(state, canvas) {
    if (!state.currentWorkflow)
        return null;
    return {
        ...state.currentWorkflow,
        steps: canvas.steps.map((cs) => ({
            id: cs.id,
            type: cs.type,
            function: cs.type === 'function' ? cs.config['function'] : undefined,
            agent: cs.type === 'agent_task' ? cs.config['agent'] : undefined,
            input: cs.config['input'] ?? '',
            depends_on: cs.dependsOn,
            output: cs.config['output'],
            timeout_seconds: cs.config['timeout_seconds'] ?? 300,
            name: cs.name,
            description: cs.config['description'],
        })),
    };
}
function runValidation(state, canvas) {
    const wf = buildWorkflowForValidation(state, canvas);
    if (!wf)
        return EMPTY_VALIDATION;
    return validateWorkflow(wf, state.availableSteps);
}
// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------
function reducer(state, action) {
    switch (action.type) {
        case 'SET_LOADING':
            return { ...state, isLoading: action.payload };
        case 'SET_SAVING':
            return { ...state, isSaving: action.payload };
        case 'SET_ERROR':
            return { ...state, error: action.payload };
        case 'SET_WORKFLOW': {
            const newState = {
                ...state,
                currentWorkflow: action.payload.workflow,
                canvasState: action.payload.canvas,
                isDirty: false,
                undoStack: [],
                redoStack: [],
                isLoading: false,
                error: null,
            };
            return { ...newState, validationErrors: runValidation(newState, action.payload.canvas) };
        }
        case 'SET_AVAILABLE_STEPS':
            return { ...state, availableSteps: action.payload };
        case 'SET_VERSIONS':
            return { ...state, versions: action.payload };
        case 'ADD_STEP': {
            const { template, position } = action.payload;
            const id = generateId();
            const defaultConfig = {};
            for (const p of template.parameters) {
                if (p.defaultValue !== undefined) {
                    defaultConfig[p.name] = p.defaultValue;
                }
            }
            const newStep = {
                id,
                templateId: template.id,
                name: template.name,
                type: template.type,
                position,
                config: defaultConfig,
                dependsOn: [],
                validationStatus: 'valid',
                validationMessages: [],
            };
            const newCanvas = {
                ...state.canvasState,
                steps: [...state.canvasState.steps, newStep],
            };
            const undo = pushUndo(state);
            const newState = {
                ...state,
                canvasState: newCanvas,
                isDirty: true,
                ...undo,
            };
            return { ...newState, validationErrors: runValidation(newState, newCanvas) };
        }
        case 'REMOVE_STEP': {
            const stepId = action.payload;
            const steps = state.canvasState.steps.filter((s) => s.id !== stepId);
            // Remove connections to/from this step
            const connections = state.canvasState.connections.filter((c) => c.fromStepId !== stepId && c.toStepId !== stepId);
            // Clean up dependsOn references in remaining steps
            const cleanedSteps = steps.map((s) => s.dependsOn.includes(stepId)
                ? { ...s, dependsOn: s.dependsOn.filter((d) => d !== stepId) }
                : s);
            const newCanvas = {
                ...state.canvasState,
                steps: cleanedSteps,
                connections,
                selectedStepId: state.canvasState.selectedStepId === stepId ? null : state.canvasState.selectedStepId,
            };
            const undo = pushUndo(state);
            const newState = {
                ...state,
                canvasState: newCanvas,
                isDirty: true,
                ...undo,
            };
            return { ...newState, validationErrors: runValidation(newState, newCanvas) };
        }
        case 'UPDATE_STEP': {
            const { stepId, config } = action.payload;
            const steps = state.canvasState.steps.map((s) => s.id === stepId ? { ...s, config: { ...s.config, ...config } } : s);
            const newCanvas = { ...state.canvasState, steps };
            const undo = pushUndo(state);
            const newState = {
                ...state,
                canvasState: newCanvas,
                isDirty: true,
                ...undo,
            };
            return { ...newState, validationErrors: runValidation(newState, newCanvas) };
        }
        case 'MOVE_STEP': {
            const { stepId, position } = action.payload;
            const steps = state.canvasState.steps.map((s) => s.id === stepId ? { ...s, position } : s);
            const newCanvas = { ...state.canvasState, steps };
            const undo = pushUndo(state);
            const newState = { ...state, canvasState: newCanvas, isDirty: true, ...undo };
            return { ...newState, validationErrors: runValidation(newState, newCanvas) };
        }
        case 'CONNECT_STEPS': {
            const { fromId, toId } = action.payload;
            // Prevent duplicate connections
            const exists = state.canvasState.connections.some((c) => c.fromStepId === fromId && c.toStepId === toId);
            if (exists)
                return state;
            const conn = { id: generateId(), fromStepId: fromId, toStepId: toId };
            const connections = [...state.canvasState.connections, conn];
            // Add to target step's dependsOn
            const steps = state.canvasState.steps.map((s) => s.id === toId && !s.dependsOn.includes(fromId)
                ? { ...s, dependsOn: [...s.dependsOn, fromId] }
                : s);
            const newCanvas = { ...state.canvasState, steps, connections };
            const undo = pushUndo(state);
            const newState = { ...state, canvasState: newCanvas, isDirty: true, ...undo };
            return { ...newState, validationErrors: runValidation(newState, newCanvas) };
        }
        case 'DISCONNECT_STEPS': {
            const { fromId, toId } = action.payload;
            const connections = state.canvasState.connections.filter((c) => !(c.fromStepId === fromId && c.toStepId === toId));
            const steps = state.canvasState.steps.map((s) => s.id === toId ? { ...s, dependsOn: s.dependsOn.filter((d) => d !== fromId) } : s);
            const newCanvas = { ...state.canvasState, steps, connections };
            const undo = pushUndo(state);
            const newState = { ...state, canvasState: newCanvas, isDirty: true, ...undo };
            return { ...newState, validationErrors: runValidation(newState, newCanvas) };
        }
        case 'SELECT_STEP':
            return { ...state, canvasState: { ...state.canvasState, selectedStepId: action.payload } };
        case 'SET_ZOOM':
            return { ...state, canvasState: { ...state.canvasState, zoom: action.payload } };
        case 'SET_PAN':
            return { ...state, canvasState: { ...state.canvasState, pan: action.payload } };
        case 'UNDO': {
            if (state.undoStack.length === 0)
                return state;
            const prev = state.undoStack[state.undoStack.length - 1];
            const undoStack = state.undoStack.slice(0, -1);
            const redoStack = [...state.redoStack, state.canvasState];
            const newState = {
                ...state,
                canvasState: prev,
                undoStack,
                redoStack,
                isDirty: true,
            };
            return { ...newState, validationErrors: runValidation(newState, prev) };
        }
        case 'REDO': {
            if (state.redoStack.length === 0)
                return state;
            const next = state.redoStack[state.redoStack.length - 1];
            const redoStack = state.redoStack.slice(0, -1);
            const undoStack = [...state.undoStack, state.canvasState];
            const newState = {
                ...state,
                canvasState: next,
                undoStack,
                redoStack,
                isDirty: true,
            };
            return { ...newState, validationErrors: runValidation(newState, next) };
        }
        case 'CLEAR_DIRTY':
            return { ...state, isDirty: false };
        case 'SET_VALIDATION':
            return { ...state, validationErrors: action.payload };
        default:
            return state;
    }
}
// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------
const WorkflowBuilderCtx = createContext(undefined);
export const useWorkflowBuilder = () => {
    const ctx = useContext(WorkflowBuilderCtx);
    if (!ctx) {
        throw new Error('useWorkflowBuilder must be used within WorkflowBuilderProvider');
    }
    return ctx;
};
export const WorkflowBuilderProvider = ({ children }) => {
    const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
    const autoSaveTimer = useRef(null);
    // -----------------------------------------------------------------------
    // Auto-save draft to localStorage (debounced 5 s)
    // -----------------------------------------------------------------------
    useEffect(() => {
        if (!state.isDirty || !state.currentWorkflow)
            return;
        if (autoSaveTimer.current) {
            clearTimeout(autoSaveTimer.current);
        }
        autoSaveTimer.current = setTimeout(() => {
            try {
                const key = draftKey(state.currentWorkflow.id);
                localStorage.setItem(key, JSON.stringify({
                    canvasState: state.canvasState,
                    savedAt: new Date().toISOString(),
                }));
            }
            catch {
                // localStorage may be full or unavailable – silently ignore
            }
        }, AUTO_SAVE_DELAY);
        return () => {
            if (autoSaveTimer.current) {
                clearTimeout(autoSaveTimer.current);
            }
        };
    }, [state.isDirty, state.canvasState, state.currentWorkflow]);
    // -----------------------------------------------------------------------
    // Step actions
    // -----------------------------------------------------------------------
    const addStep = useCallback((template, position) => {
        dispatch({ type: 'ADD_STEP', payload: { template, position } });
    }, []);
    const removeStep = useCallback((stepId) => {
        dispatch({ type: 'REMOVE_STEP', payload: stepId });
    }, []);
    const updateStep = useCallback((stepId, config) => {
        dispatch({ type: 'UPDATE_STEP', payload: { stepId, config } });
    }, []);
    const moveStep = useCallback((stepId, position) => {
        dispatch({ type: 'MOVE_STEP', payload: { stepId, position } });
    }, []);
    // -----------------------------------------------------------------------
    // Connection actions
    // -----------------------------------------------------------------------
    const connectSteps = useCallback((fromId, toId) => {
        dispatch({ type: 'CONNECT_STEPS', payload: { fromId, toId } });
    }, []);
    const disconnectSteps = useCallback((fromId, toId) => {
        dispatch({ type: 'DISCONNECT_STEPS', payload: { fromId, toId } });
    }, []);
    // -----------------------------------------------------------------------
    // Selection / viewport
    // -----------------------------------------------------------------------
    const selectStep = useCallback((stepId) => {
        dispatch({ type: 'SELECT_STEP', payload: stepId });
    }, []);
    const setZoom = useCallback((zoom) => {
        dispatch({ type: 'SET_ZOOM', payload: zoom });
    }, []);
    const setPan = useCallback((pan) => {
        dispatch({ type: 'SET_PAN', payload: pan });
    }, []);
    // -----------------------------------------------------------------------
    // Undo / redo
    // -----------------------------------------------------------------------
    const undo = useCallback(() => dispatch({ type: 'UNDO' }), []);
    const redo = useCallback(() => dispatch({ type: 'REDO' }), []);
    // -----------------------------------------------------------------------
    // Persistence – save
    // -----------------------------------------------------------------------
    const saveWorkflow = useCallback(async () => {
        if (!state.currentWorkflow)
            return;
        dispatch({ type: 'SET_SAVING', payload: true });
        dispatch({ type: 'SET_ERROR', payload: null });
        try {
            const wfDef = buildWorkflowForValidation(state, state.canvasState);
            if (!wfDef)
                throw new Error('No workflow to save');
            // Attach UI metadata
            const positions = {};
            for (const s of state.canvasState.steps) {
                positions[s.id] = s.position;
            }
            wfDef.uiMetadata = {
                ...wfDef.uiMetadata,
                canvasLayout: {
                    steps: positions,
                    zoom: state.canvasState.zoom,
                    pan: state.canvasState.pan,
                },
            };
            let saved;
            if (state.currentWorkflow.id) {
                saved = await workflowBuilderApi.updateWorkflow(state.currentWorkflow.id, wfDef);
            }
            else {
                saved = await workflowBuilderApi.createWorkflow(wfDef);
            }
            // Refresh versions
            const versions = await workflowBuilderApi.listVersions(saved.id);
            dispatch({
                type: 'SET_WORKFLOW',
                payload: { workflow: saved, canvas: state.canvasState },
            });
            dispatch({ type: 'SET_VERSIONS', payload: versions });
            dispatch({ type: 'CLEAR_DIRTY' });
            // Clear draft from localStorage
            try {
                localStorage.removeItem(draftKey(saved.id));
            }
            catch {
                // ignore
            }
        }
        catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to save workflow';
            dispatch({ type: 'SET_ERROR', payload: message });
        }
        finally {
            dispatch({ type: 'SET_SAVING', payload: false });
        }
    }, [state]);
    // -----------------------------------------------------------------------
    // Persistence – load
    // -----------------------------------------------------------------------
    const loadWorkflow = useCallback(async (workflowId) => {
        dispatch({ type: 'SET_LOADING', payload: true });
        dispatch({ type: 'SET_ERROR', payload: null });
        try {
            // Fetch workflow, step library, and versions in parallel
            const [workflow, stepLibrary, versions] = await Promise.all([
                workflowBuilderApi.getWorkflow(workflowId),
                workflowBuilderApi.getStepLibrary(),
                workflowBuilderApi.listVersions(workflowId),
            ]);
            dispatch({ type: 'SET_AVAILABLE_STEPS', payload: stepLibrary.templates });
            dispatch({ type: 'SET_VERSIONS', payload: versions });
            // Check for a draft in localStorage
            let draftCanvas = null;
            try {
                const raw = localStorage.getItem(draftKey(workflowId));
                if (raw) {
                    const parsed = JSON.parse(raw);
                    if (parsed?.canvasState) {
                        draftCanvas = parsed.canvasState;
                    }
                }
            }
            catch {
                // ignore corrupt draft
            }
            // Use draft canvas if available, otherwise build from workflow definition
            const canvas = draftCanvas ?? buildCanvasFromWorkflow(workflow);
            dispatch({ type: 'SET_WORKFLOW', payload: { workflow, canvas } });
        }
        catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to load workflow';
            dispatch({ type: 'SET_ERROR', payload: message });
            dispatch({ type: 'SET_LOADING', payload: false });
        }
    }, []);
    // -----------------------------------------------------------------------
    // Value
    // -----------------------------------------------------------------------
    const value = {
        state,
        addStep,
        removeStep,
        updateStep,
        moveStep,
        connectSteps,
        disconnectSteps,
        selectStep,
        setZoom,
        setPan,
        saveWorkflow,
        loadWorkflow,
        undo,
        redo,
        canUndo: state.undoStack.length > 0,
        canRedo: state.redoStack.length > 0,
    };
    return (_jsx(WorkflowBuilderCtx.Provider, { value: value, children: children }));
};
// ---------------------------------------------------------------------------
// Helpers – build canvas from a loaded workflow
// ---------------------------------------------------------------------------
function buildCanvasFromWorkflow(workflow) {
    const layoutPositions = workflow.uiMetadata?.canvasLayout?.steps ?? {};
    const hasPositions = Object.keys(layoutPositions).length > 0;
    // If no saved positions, compute auto-layout
    const positions = hasPositions ? layoutPositions : autoLayout(workflow);
    const steps = workflow.steps.map((s) => ({
        id: s.id,
        templateId: s.function ?? s.agent ?? s.type,
        name: s.name,
        type: s.type,
        position: positions[s.id] ?? { x: 0, y: 0 },
        config: {
            ...(typeof s.input === 'object' && s.input !== null ? s.input : {}),
            function: s.function,
            agent: s.agent,
            output: s.output,
            timeout_seconds: s.timeout_seconds,
            description: s.description,
        },
        dependsOn: s.depends_on,
        validationStatus: 'valid',
        validationMessages: [],
    }));
    // Build connections from depends_on relationships
    const connections = [];
    for (const step of workflow.steps) {
        for (const depId of step.depends_on) {
            connections.push({
                id: generateId(),
                fromStepId: depId,
                toStepId: step.id,
            });
        }
    }
    return {
        steps,
        connections,
        selectedStepId: null,
        zoom: workflow.uiMetadata?.canvasLayout?.zoom ?? 1,
        pan: workflow.uiMetadata?.canvasLayout?.pan ?? { x: 0, y: 0 },
    };
}
